import type { JobRunSummaryDto } from '@/api/generated/schemas';
import type { NavigationKernel } from '@/shared/navigation/core';

/**
 * Registr výsledkových akcí jobů — čistě frontendová udělátka (schválené zadání): submodul si
 * ke svému `jobType` zaregistruje jednu či víc akcí (tlačítek), které se u dokončeného běhu
 * ukážou v řádku centrálního přehledu a v patičce detailu. Jobs modul o konkrétních akcích
 * nic neví; data si akce bere z opaque `run.contextData` (JSON plněný plan factory na BE).
 */

export interface JobResultActionContext {
  run: JobRunSummaryDto;
  /** Parsované `run.contextData`; null když chybí nebo není validní JSON. */
  contextData: unknown;
  /** Navigační kernel — akce typicky otevírají agendy (dodává renderující komponenta). */
  kernel: NavigationKernel;
}

export interface JobResultAction {
  /** Stabilní klíč akce (React key). */
  key: string;
  /** i18n klíč popisku — překládá se v místě renderu přes `t(labelKey)` (plný klíč s namespace). */
  labelKey: string;
  onActivate: (context: JobResultActionContext) => void;
}

const registry = new Map<string, JobResultAction[]>();

/** Zaregistruje akce pro daný jobType (module-init submodulu; opakovaná registrace přepisuje). */
export function registerJobResultActions(jobType: string, actions: JobResultAction[]): void {
  registry.set(jobType, actions);
}

/** Akce pro daný běh (prázdné pole, když jobType žádné nemá). */
export function getJobResultActions(jobType: string): readonly JobResultAction[] {
  return registry.get(jobType) ?? [];
}

/** Bezpečně parsuje `run.contextData` a postaví kontext pro handler akce. */
export function toJobResultActionContext(
  run: JobRunSummaryDto,
  kernel: NavigationKernel
): JobResultActionContext {
  let contextData: unknown = null;
  if (run.contextData) {
    try {
      contextData = JSON.parse(run.contextData);
    } catch {
      contextData = null;
    }
  }
  return { run, contextData, kernel };
}
